import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, BadgeCheck, RefreshCw, Calculator, ShieldCheck, Check, AlertCircle } from "lucide-react";

import { API } from "../config";
import { tx } from "../lib/tx";

// Generate a simple math challenge
const generateChallenge = () => {
  const num1 = Math.floor(Math.random() * 20) + 1;
  const num2 = Math.floor(Math.random() * 20) + 1;
  const operators = ['+', '-', '×'];
  const operator = operators[Math.floor(Math.random() * operators.length)];
  
  let answer;
  switch (operator) {
    case '+':
      answer = num1 + num2;
      break;
    case '-':
      answer = Math.max(num1, num2) - Math.min(num1, num2);
      return {
        question: `${Math.max(num1, num2)} - ${Math.min(num1, num2)} = ?`,
        answer: answer.toString()
      };
    case '×':
      const a = Math.floor(Math.random() * 10) + 1;
      const b = Math.floor(Math.random() * 10) + 1;
      return {
        question: `${a} × ${b} = ?`,
        answer: (a * b).toString()
      };
    default:
      answer = num1 + num2;
  }
  
  return {
    question: `${num1} ${operator} ${num2} = ?`,
    answer: answer.toString()
  };
};

export default function VerificationSimple() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    setChallenge(generateChallenge());
  }, []);

  const handleRefresh = () => {
    setChallenge(generateChallenge());
    setUserAnswer("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!userAnswer.trim()) {
      setError(tx("Veuillez entrer votre réponse"));
      return;
    }

    // Check answer locally first
    if (userAnswer.trim() !== challenge.answer) {
      setAttempts(prev => prev + 1);
      setError(tx("Réponse incorrecte. Essayez encore !"));
      if (attempts >= 2) {
        handleRefresh();
        setAttempts(0);
      }
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/users/verify-simple`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          challenge_answer: userAnswer.trim()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        setUser(prev => ({ ...prev, is_verified: true }));
        // Update localStorage
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        stored.is_verified = true;
        localStorage.setItem("user", JSON.stringify(stored));
        
        setTimeout(() => {
          navigate("/profile");
        }, 2000);
      } else {
        setError(data.detail || tx("Erreur de vérification"));
      }
    } catch (error) {
      console.error("Verification error:", error);
      setError(tx("Erreur de connexion. Réessayez."));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center animate-fade-in">
          <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <BadgeCheck className="w-12 h-12 text-green-500" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-white mb-2">
            {tx("Profil vérifié ! 🎉")}
          </h2>
          <p className="text-muted-foreground mb-4">
            {tx("Vous avez maintenant le badge vérifié sur votre profil.")}
          </p>
          <div className="flex items-center justify-center gap-2 text-green-500">
            <Check className="w-5 h-5" />
            <span>{tx("Redirection en cours...")}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="verification-simple-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-surface h-16 flex items-center px-4 gap-3">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(-1)}
          className="text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-6 h-6" />
        </Button>
        <h1 className="font-heading text-xl font-semibold text-white">
          {tx("Vérification du profil")}
        </h1>
      </header>

      <div className="pt-20 pb-8 px-4">
        <div className="max-w-md mx-auto">
          {/* Info Card */}
          <div className="bg-card/50 border border-white/10 rounded-3xl p-6 mb-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-green-500/20 flex items-center justify-center">
                <ShieldCheck className="w-7 h-7 text-green-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-white">{tx("Obtenez le badge vérifié")}</h2>
                <p className="text-sm text-muted-foreground">{tx("Prouvez que vous êtes un humain")}</p>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>{tx("Augmentez votre visibilité")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>{tx("Gagnez la confiance des autres membres")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>{tx("Accédez à plus de fonctionnalités")}</span>
              </div>
            </div>
          </div>

          {/* Challenge Card */}
          <div className="bg-card/50 border border-white/10 rounded-3xl p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Calculator className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-1">
                {tx("Résolvez ce calcul")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {tx("Prouvez que vous n'êtes pas un robot")}
              </p>
            </div>

            {/* Challenge Display */}
            {challenge && (
              <div className="bg-muted/30 rounded-2xl p-6 mb-6 text-center">
                <p className="text-3xl font-bold text-white mb-2 font-mono">
                  {challenge.question}
                </p>
                <button
                  onClick={handleRefresh}
                  className="text-sm text-primary flex items-center gap-1 mx-auto hover:underline"
                  type="button"
                >
                  <RefreshCw className="w-3 h-3" />
                  {tx("Nouveau calcul")}
                </button>
              </div>
            )}

            {/* Answer Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={tx("Votre réponse")}
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value.replace(/[^0-9-]/g, ''))}
                  className="h-14 text-center text-2xl font-bold bg-muted/50 border-white/10 text-white rounded-xl"
                  data-testid="answer-input"
                  autoFocus
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-xl">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !userAnswer.trim()}
                className="w-full h-14 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold text-lg"
                data-testid="verify-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{tx("Vérification...")}</span>
                  </div>
                ) : (
                  <>
                    <BadgeCheck className="w-5 h-5 mr-2" />
                    {tx("Vérifier mon profil")}
                  </>
                )}
              </Button>
            </form>

            {attempts > 0 && (
              <p className="text-center text-sm text-muted-foreground mt-4">
                {3 - attempts} {tx("tentative(s) restante(s) avant nouveau calcul")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
